import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  TextField,
  Grid,
  Paper,
  Alert,
  CircularProgress,
  MenuItem,
  InputLabel,
  FormControl,
  Select,
  Divider,
  SelectChangeEvent,
  FormHelperText,
  Stack,
  Switch,
  FormControlLabel
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import api from '../api/axios';

interface PatientPhoneNumber {
  number: string;
  phone_type: string;
}

interface PatientData {
  first_name: string;
  last_name: string;
  email: string;
  title?: string;
  preferred_first_name?: string;
  date_of_birth?: string | null;
  address_1?: string;
  address_2?: string;
  address_3?: string;
  city?: string;
  state?: string;
  post_code?: string;
  country?: string;
  country_code?: string;
  emergency_contact?: string;
  occupation?: string;
  notes?: string;
  appointment_notes?: string;
  receives_confirmation_emails?: boolean;
  receives_cancellation_emails?: boolean;
  reminder_type?: string;
  patient_phone_numbers?: PatientPhoneNumber[];
}

interface CreatePatientFormProps {
  onSuccess?: (patient: any) => void;
  onCancel?: () => void;
}

const phoneTypes = [
  'Home',
  'Work',
  'Mobile',
  'Fax',
  'Pager'
];

const australianStates = [
  'ACT',
  'NSW',
  'NT',
  'QLD',
  'SA',
  'TAS',
  'VIC',
  'WA'
];

const reminderTypes = [
  'None',
  'SMS',
  'Email',
  'SMS & Email'
];

const CreatePatientForm: React.FC<CreatePatientFormProps> = ({ onSuccess, onCancel }) => {
  const [patientData, setPatientData] = useState<PatientData>({
    first_name: '',
    last_name: '',
    email: '',
    country: 'Australia',
    country_code: 'AU',
    receives_confirmation_emails: true,
    receives_cancellation_emails: true,
    reminder_type: 'None',
    patient_phone_numbers: [{ number: '', phone_type: 'Mobile' }]
  });
  
  const [dateOfBirth, setDateOfBirth] = useState<Date | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setPatientData(prev => ({ ...prev, [name]: value }));
  };
  
  const handleSwitchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = e.target;
    setPatientData(prev => ({ ...prev, [name]: checked }));
  };
  
  const handleSelectChange = (e: SelectChangeEvent) => {
    const { name, value } = e.target;
    setPatientData(prev => ({ ...prev, [name]: value }));
  };
  
  const handlePhoneChange = (index: number, field: string, value: string) => {
    const updatedPhones = [...(patientData.patient_phone_numbers || [])];
    updatedPhones[index] = { 
      ...updatedPhones[index], 
      [field]: value 
    };
    
    setPatientData(prev => ({
      ...prev,
      patient_phone_numbers: updatedPhones
    }));
  };
  
  const addPhoneNumber = () => {
    setPatientData(prev => ({
      ...prev,
      patient_phone_numbers: [
        ...(prev.patient_phone_numbers || []),
        { number: '', phone_type: 'Mobile' }
      ]
    }));
  };
  
  const removePhoneNumber = (index: number) => {
    const updatedPhones = [...(patientData.patient_phone_numbers || [])];
    updatedPhones.splice(index, 1);
    
    setPatientData(prev => ({
      ...prev,
      patient_phone_numbers: updatedPhones
    }));
  };
  
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);
    setSuccess(false);
    
    // Format date of birth for API
    let formattedData = { ...patientData };
    if (dateOfBirth) {
      formattedData.date_of_birth = dateOfBirth.toISOString().split('T')[0];
    }
    
    try {
      const response = await api.post('/api/create-cliniko-patient', formattedData);
      
      setSuccess(true);
      // Reset form after success
      setPatientData({
        first_name: '',
        last_name: '',
        email: '',
        country: 'Australia',
        country_code: 'AU',
        receives_confirmation_emails: true,
        receives_cancellation_emails: true,
        reminder_type: 'None',
        patient_phone_numbers: [{ number: '', phone_type: 'Mobile' }]
      });
      setDateOfBirth(null);
      
      if (onSuccess) {
        onSuccess(response.data);
      }
    } catch (err: any) {
      console.error('Error creating patient:', err);
      setError(
        err.response?.data?.error || 
        err.response?.data?.details || 
        'Failed to create patient'
      );
    } finally {
      setIsLoading(false);
    }
  };
  
  return (
    <Paper 
      elevation={2} 
      sx={{ 
        p: { xs: 2, sm: 3 },
        mb: 4,
        maxWidth: '100%',
        overflow: 'hidden'
      }}
    >
      <Typography variant="h5" gutterBottom mb={2}>
        Create New Client
      </Typography>
      
      {success && (
        <Alert severity="success" sx={{ mb: 2 }}>
          Client created successfully
        </Alert>
      )}
      
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      
      <form onSubmit={handleSubmit}>
        <Grid container spacing={2}>
          {/* Basic Information */}
          <Grid item xs={12}>
            <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>
              Basic Information
            </Typography>
          </Grid>
          
          <Grid item xs={12} sm={3}>
            <TextField
              select
              name="title"
              label="Title"
              value={patientData.title || ''}
              onChange={handleSelectChange}
              fullWidth
              size="small"
            >
              <MenuItem value="">None</MenuItem>
              <MenuItem value="Mr">Mr</MenuItem>
              <MenuItem value="Mrs">Mrs</MenuItem>
              <MenuItem value="Ms">Ms</MenuItem>
              <MenuItem value="Miss">Miss</MenuItem>
              <MenuItem value="Dr">Dr</MenuItem>
              <MenuItem value="Prof">Prof</MenuItem>
            </TextField>
          </Grid>
          
          <Grid item xs={12} sm={4.5}>
            <TextField
              required
              name="first_name"
              label="First Name"
              value={patientData.first_name}
              onChange={handleChange}
              fullWidth
              size="small"
            />
          </Grid>
          
          <Grid item xs={12} sm={4.5}>
            <TextField
              required
              name="last_name"
              label="Last Name"
              value={patientData.last_name}
              onChange={handleChange}
              fullWidth
              size="small"
            />
          </Grid>
          
          <Grid item xs={12} sm={6}>
            <TextField
              name="preferred_first_name"
              label="Preferred Name"
              value={patientData.preferred_first_name || ''}
              onChange={handleChange}
              fullWidth
              size="small"
            />
          </Grid>
          
          <Grid item xs={12} sm={6}>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DatePicker
                label="Date of Birth"
                value={dateOfBirth}
                onChange={(newValue) => setDateOfBirth(newValue)}
                renderInput={(params) => (
                  <TextField {...params} size="small" fullWidth />
                )}
              />
            </LocalizationProvider>
          </Grid>
          
          {/* Contact Information */}
          <Grid item xs={12}>
            <Divider sx={{ my: 1 }} />
            <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mt: 2, mb: 1 }}>
              Contact Information
            </Typography>
          </Grid>
          
          <Grid item xs={12}>
            <TextField
              name="email"
              label="Email"
              type="email"
              value={patientData.email}
              onChange={handleChange}
              fullWidth
              size="small"
            />
          </Grid>
          
          {/* Phone Numbers */}
          <Grid item xs={12}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Phone Numbers
            </Typography>
            
            {patientData.patient_phone_numbers?.map((phone, index) => (
              <Box key={index} sx={{ display: 'flex', gap: 1, mb: 1 }}>
                <TextField
                  name={`phone-${index}`}
                  label="Phone Number"
                  value={phone.number}
                  onChange={(e) => handlePhoneChange(index, 'number', e.target.value)}
                  fullWidth
                  size="small"
                />
                
                <FormControl sx={{ minWidth: 120 }} size="small">
                  <InputLabel id={`phone-type-label-${index}`}>Type</InputLabel>
                  <Select
                    labelId={`phone-type-label-${index}`}
                    value={phone.phone_type}
                    label="Type"
                    onChange={(e) => handlePhoneChange(index, 'phone_type', e.target.value)}
                  >
                    {phoneTypes.map(type => (
                      <MenuItem key={type} value={type}>{type}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
                
                {patientData.patient_phone_numbers && patientData.patient_phone_numbers.length > 1 && (
                  <Button 
                    color="error" 
                    onClick={() => removePhoneNumber(index)}
                    sx={{ minWidth: 'auto' }}
                  >
                    Remove
                  </Button>
                )}
              </Box>
            ))}
            
            <Button 
              onClick={addPhoneNumber} 
              variant="outlined" 
              size="small"
              sx={{ mt: 1 }}
            >
              Add Phone Number
            </Button>
          </Grid>
          
          <Grid item xs={12}>
            <TextField
              name="emergency_contact"
              label="Emergency Contact"
              value={patientData.emergency_contact || ''}
              onChange={handleChange}
              fullWidth
              size="small"
            />
          </Grid>
          
          {/* Address */}
          <Grid item xs={12}>
            <Divider sx={{ my: 1 }} />
            <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mt: 2, mb: 1 }}>
              Address
            </Typography>
          </Grid>
          
          <Grid item xs={12}>
            <TextField
              name="address_1"
              label="Address Line 1"
              value={patientData.address_1 || ''}
              onChange={handleChange}
              fullWidth
              size="small"
            />
          </Grid>
          
          <Grid item xs={12}>
            <TextField
              name="address_2"
              label="Address Line 2"
              value={patientData.address_2 || ''}
              onChange={handleChange}
              fullWidth
              size="small"
            />
          </Grid>
          
          <Grid item xs={12}>
            <TextField
              name="address_3"
              label="Address Line 3"
              value={patientData.address_3 || ''}
              onChange={handleChange}
              fullWidth
              size="small"
            />
          </Grid>
          
          <Grid item xs={12} sm={6}>
            <TextField
              name="city"
              label="City/Suburb"
              value={patientData.city || ''}
              onChange={handleChange}
              fullWidth
              size="small"
            />
          </Grid>
          
          <Grid item xs={12} sm={3}>
            <FormControl fullWidth size="small">
              <InputLabel id="state-label">State</InputLabel>
              <Select
                labelId="state-label"
                name="state"
                value={patientData.state || ''}
                label="State"
                onChange={handleSelectChange}
              >
                <MenuItem value="">Select a state</MenuItem>
                {australianStates.map(state => (
                  <MenuItem key={state} value={state}>{state}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={12} sm={3}>
            <TextField
              name="post_code"
              label="Postal Code"
              value={patientData.post_code || ''}
              onChange={handleChange}
              fullWidth
              size="small"
            />
          </Grid>
          
          {/* Hidden fields for Australia */}
          <input type="hidden" name="country" value="Australia" />
          <input type="hidden" name="country_code" value="AU" />
          
          {/* Additional Information */}
          <Grid item xs={12}>
            <Divider sx={{ my: 1 }} />
            <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mt: 2, mb: 1 }}>
              Additional Information
            </Typography>
          </Grid>
          
          <Grid item xs={12}>
            <TextField
              name="occupation"
              label="Occupation"
              value={patientData.occupation || ''}
              onChange={handleChange}
              fullWidth
              size="small"
            />
          </Grid>
          
          <Grid item xs={12}>
            <TextField
              name="notes"
              label="Notes"
              value={patientData.notes || ''}
              onChange={handleChange}
              fullWidth
              multiline
              rows={3}
              size="small"
            />
          </Grid>
          
          <Grid item xs={12}>
            <TextField
              name="appointment_notes"
              label="Appointment Notes"
              value={patientData.appointment_notes || ''}
              onChange={handleChange}
              fullWidth
              multiline
              rows={3}
              size="small"
            />
          </Grid>
          
          {/* Communication Preferences */}
          <Grid item xs={12}>
            <Divider sx={{ my: 1 }} />
            <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mt: 2, mb: 1 }}>
              Communication Preferences
            </Typography>
          </Grid>
          
          <Grid item xs={12} sm={6}>
            <FormControlLabel
              control={
                <Switch
                  checked={patientData.receives_confirmation_emails || false}
                  onChange={handleSwitchChange}
                  name="receives_confirmation_emails"
                />
              }
              label="Receive Confirmation Emails"
            />
          </Grid>
          
          <Grid item xs={12} sm={6}>
            <FormControlLabel
              control={
                <Switch
                  checked={patientData.receives_cancellation_emails || false}
                  onChange={handleSwitchChange}
                  name="receives_cancellation_emails"
                />
              }
              label="Receive Cancellation Emails"
            />
          </Grid>
          
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth size="small">
              <InputLabel id="reminder-type-label">Reminder Type</InputLabel>
              <Select
                labelId="reminder-type-label"
                name="reminder_type"
                value={patientData.reminder_type || 'None'}
                label="Reminder Type"
                onChange={handleSelectChange}
              >
                {reminderTypes.map(type => (
                  <MenuItem key={type} value={type}>{type}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          
          {/* Form Actions */}
          <Grid item xs={12}>
            <Divider sx={{ my: 2 }} />
            <Stack 
              direction="row" 
              spacing={2} 
              justifyContent="flex-end"
              sx={{ mt: 2 }}
            >
              {onCancel && (
                <Button
                  variant="outlined"
                  onClick={onCancel}
                  disabled={isLoading}
                >
                  Cancel
                </Button>
              )}
              
              <Button
                type="submit"
                variant="contained"
                color="primary"
                disabled={isLoading || !patientData.first_name || !patientData.last_name}
              >
                {isLoading ? <CircularProgress size={24} /> : "Create Client"}
              </Button>
            </Stack>
          </Grid>
        </Grid>
      </form>
    </Paper>
  );
};

export default CreatePatientForm;